import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import { getApplicationByFolio } from "@/lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_postdoc-judicial/artifacts/ppjh7riu_EJE.png";

const MODALIDAD_LABEL = {
  posdoctorado: "Posdoctorado",
  estancia: "Estancia de Investigación",
  estancia_conjunta: "Estancia Conjunta",
};

export default function Comprobante() {
  const { folio } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!folio) return;
    getApplicationByFolio(folio)
      .then(setApp)
      .catch(() => toast.error("No se pudo cargar el folio"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folio]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Cargando comprobante…
      </div>
    );
  }

  if (!app) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <p className="text-slate-600 mb-4">Folio no encontrado.</p>
        <Link to="/" className="btn-primary-eje">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const fullName = [app.nombre, app.apellido_paterno, app.apellido_materno]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="min-h-screen bg-white print:bg-white">
      {/* Top bar (hidden on print) */}
      <div className="bg-slate-50 border-b border-slate-200 py-3 px-4 print:hidden">
        <div className="container-narrow flex items-center justify-between">
          <Link
            to="/"
            className="text-sm text-slate-700 hover:text-eje inline-flex items-center gap-1.5"
            data-testid="comprobante-back"
          >
            <ArrowLeft className="w-4 h-4" /> Inicio
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-eje text-white rounded-sm hover:bg-eje-800 transition-colors"
            data-testid="comprobante-print"
          >
            <Printer className="w-4 h-4" /> Imprimir o guardar PDF
          </button>
        </div>
      </div>

      {/* Comprobante document */}
      <main
        className="container-narrow py-12 print:py-6"
        data-testid="comprobante-document"
      >
        {/* Header */}
        <header className="border-b-2 border-eje pb-6 mb-8 flex items-center gap-5">
          <img src={LOGO_URL} alt="EJE" className="h-20 w-20 object-contain" />
          <div className="flex-1">
            <div className="overline text-eje mb-1">
              Tribunal Electoral del Poder Judicial de la Federación
            </div>
            <h1 className="font-serif text-2xl md:text-3xl text-eje leading-tight">
              Escuela Judicial Electoral
            </h1>
            <div className="text-sm text-slate-600 mt-1">
              Posdoctorado y Estancias de Investigación · Convocatoria 2026
            </div>
          </div>
          <div className="text-right">
            <div className="overline text-slate-500">Folio</div>
            <div
              className="font-serif text-3xl text-eje"
              data-testid="comprobante-folio"
            >
              {app.folio}
            </div>
          </div>
        </header>

        {/* Status */}
        <div className="bg-eje/5 border border-eje/15 p-5 mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            <div>
              <div className="font-serif text-xl text-eje">
                Comprobante de inscripción
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Estado:{" "}
                <span className="font-medium capitalize">
                  {app.status === "enviada" ? "Enviada" : "En curso"}
                </span>{" "}
                ·{" "}
                {app.submitted_at
                  ? `Enviada el ${new Date(app.submitted_at).toLocaleString("es-MX")}`
                  : `Iniciada el ${new Date(app.created_at).toLocaleString("es-MX")}`}
              </div>
            </div>
          </div>
        </div>

        {/* Datos del aspirante */}
        <Section title="Datos del aspirante">
          <Row label="Nombre completo" value={fullName} />
          <Row label="Correo electrónico" value={app.email} />
          <Row label="Teléfono" value={app.telefono} />
          <Row label="Nacionalidad" value={app.nacionalidad} />
          <Row label="País / Ciudad" value={`${app.pais} · ${app.ciudad}`} />
          <Row label="Identificación oficial" value={app.identificacion_numero} />
        </Section>

        <Section title="Modalidad seleccionada">
          <Row
            label="Modalidad"
            value={MODALIDAD_LABEL[app.modalidad] || app.modalidad}
          />
          {(app.modalidad === "posdoctorado" ||
            app.modalidad === "estancia_conjunta") && (
            <Row
              label="Modo de impartición"
              value={
                app.modalidad_presencial
                  ? app.modalidad_presencial.replace("_", " ")
                  : "—"
              }
              cap
            />
          )}
        </Section>

        <Section title="Formación académica">
          <Row label="Grado máximo" value={app.grado_maximo} cap />
          <Row label="Título obtenido" value={app.titulo_obtenido} />
          <Row label="Institución" value={app.institucion} />
          <Row label="Año de titulación" value={app.anio_titulacion} />
          <Row label="Área de conocimiento" value={app.area_conocimiento} />
          {app.cedula_profesional && (
            <Row label="Cédula profesional" value={app.cedula_profesional} />
          )}
        </Section>

        <Section title={`Documentos presentados (${app.documents?.length || 0})`}>
          {!app.documents?.length ? (
            <p className="text-sm text-slate-500">Sin documentos cargados.</p>
          ) : (
            <ol className="list-decimal pl-6 text-sm text-slate-800 space-y-1">
              {app.documents.map((d) => (
                <li
                  key={d.storage_path}
                  className="flex items-start gap-2"
                  data-testid={`comprobante-doc-${d.doc_key}`}
                >
                  <FileText className="w-4 h-4 text-slate-500 mt-0.5" />
                  <span className="capitalize">
                    {d.doc_key.replace(/_/g, " ")}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Section>

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-slate-600">
            <div>
              <div className="overline text-eje mb-2">Importante</div>
              <p className="leading-relaxed">
                Este comprobante valida el registro electrónico del aspirante en
                la convocatoria 2026. Consérvelo como respaldo de su
                inscripción. Para revisión de resultados o aclaraciones,
                conserve el folio asignado.
              </p>
            </div>
            <div>
              <div className="overline text-eje mb-2">Contacto</div>
              <p>
                Correo:{" "}
                <a
                  href="mailto:pei.eje@te.gob.mx"
                  className="underline text-eje"
                >
                  pei.eje@te.gob.mx
                </a>
              </p>
              <p>
                Sitio:{" "}
                <a
                  href="https://www.te.gob.mx/eje/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-eje"
                >
                  www.te.gob.mx/eje/
                </a>
              </p>
            </div>
          </div>
          <div className="mt-6 text-[10px] text-slate-400 text-center">
            Generado el {new Date().toLocaleString("es-MX")} · Folio {app.folio}
          </div>
        </footer>
      </main>

      <style>{`
        @media print {
          @page { size: A4; margin: 1.5cm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="overline text-eje mb-3 pb-2 border-b border-slate-200">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
        {children}
      </div>
    </section>
  );
}

function Row({ label, value, cap = false }) {
  return (
    <div className="text-sm flex gap-3">
      <span className="text-slate-500 min-w-[140px]">{label}:</span>
      <span className={`text-slate-800 ${cap ? "capitalize" : ""}`}>
        {value || "—"}
      </span>
    </div>
  );
}
